import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { demandsApi } from '../api/demandsApi';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { useThemeContext } from '../hooks/contexts/useThemeContext';
import type { DemandStatus } from '../types/engineering';

const STATUS_CARDS: { status: DemandStatus; label: string }[] = [
  { status: 'BACKLOG', label: 'BACKLOG' },
  { status: 'PLANNING', label: 'PLANNING' },
  { status: 'INPROGRESS', label: 'IN PROGRESS' },
  { status: 'WAITING_REVIEW', label: 'WAITING REVIEW' },
];

const PIE_COLORS = [
  '#00243d', '#0073ab', '#00a3e0', '#5bc2e7',
  '#42b0d5', '#7ab648', '#f0ab00', '#e04403',
];

export const HomePage = () => {
  const { themeVariant } = useThemeContext();
  const isDark = themeVariant === 'dark';

  const chartTextColor = isDark ? '#94a3b8' : '#4a5568';
  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const barFill = isDark ? '#38bdf8' : '#00243d';
  const tooltipStyle = {
    backgroundColor: isDark ? '#1e293b' : '#fff',
    borderColor: isDark ? '#334155' : '#e2e8f0',
    color: isDark ? '#e2e8f0' : '#1a202c',
  };

  const { data: statusCounts, isLoading: loadingStatus } = useQuery({
    queryKey: ['demands', 'stats', 'by-status'],
    queryFn: () => demandsApi.getStatusCounts(),
  });

  const { data: openBySystem, isLoading: loadingSystem } = useQuery({
    queryKey: ['demands', 'stats', 'open-by-system'],
    queryFn: () => demandsApi.getOpenBySystem(),
  });

  const { data: doneByMonth, isLoading: loadingMonth } = useQuery({
    queryKey: ['demands', 'stats', 'done-by-month'],
    queryFn: () => demandsApi.getDoneByMonth(),
  });

  const isLoading = loadingStatus || loadingSystem || loadingMonth;

  if (isLoading) return <LoadingIndicator />;

  const countMap = Object.fromEntries(
    (statusCounts ?? []).map((s) => [s.status, s.count]),
  );

  return (
    <div>
      <div className="page-header">
        <p>Overview of engineering demands</p>
      </div>

      {/* Row 1 – status stat cards */}
      <div className="stat-cards-row">
        {STATUS_CARDS.map(({ status, label }) => (
          <div key={status} className="stat-card">
            <div className="stat-card__value">{countMap[status] ?? 0}</div>
            <div className="stat-card__label">{label}</div>
          </div>
        ))}
      </div>

      {/* Row 2 – charts */}
      <div className="charts-row">
        {/* Pie – open demands by system */}
        <div className="chart-card">
          <div className="chart-card__title">Demands Open by System</div>
          {openBySystem && openBySystem.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={openBySystem}
                  dataKey="count"
                  nameKey="system"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ cx, cy, midAngle, outerRadius: r, name, percent }) => {
                    const RADIAN = Math.PI / 180;
                    const radius = r + 28;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={chartTextColor}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline="central"
                        fontSize={11}
                      >
                        {`${name ?? '—'} (${(percent * 100).toFixed(0)}%)`}
                      </text>
                    );
                  }}
                >
                  {openBySystem.map((_, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [value, 'Demands']}
                  contentStyle={tooltipStyle}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">No open demands.</div>
          )}
        </div>

        {/* Bar – DONE demands by month */}
        <div className="chart-card">
          <div className="chart-card__title">Demands Done by Month</div>
          {doneByMonth && doneByMonth.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={doneByMonth} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: chartTextColor }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: chartTextColor }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Done" fill={barFill} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">No completed demands in the last 4 months.</div>
          )}
        </div>
      </div>
    </div>
  );
};
